
import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import { namespace, Getter, Mutation, Action } from "vuex-class"

import ApiFormattingRuleSettings from "@/services/api_FormattingRuleSettings.ts"

import { CaseColumn, caseColumns } from "@/types/caseColumns"
import { FormattingRule, operatorsByFieldType, operatorLabels, DataType, Operator } from "@/types/insights"
import { FormattingRuleSetting } from "@/types/insights/formattingRuleSetting"

// @ts-ignore
import draggable from "vuedraggable"
import vSelect from "vue-select"
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import ParameterValue from "./ParameterValue.vue"

@Component({
	components: {
		"v-select": vSelect,
		"parameter-value": ParameterValue,
		"scrollbar": VuePerfectScrollbar,
		draggable
	}
})
export default class ColumnFormatting extends Vue {
	@Prop({ default: () => null })
	column!: CaseColumn | null

	@Prop({ default: () => [] })
	columns!: CaseColumn[]

	@Prop({ default: () => [] })
	value!: FormattingRule[]

	@Prop({ default: () => false })
	disableGlobal!: boolean

	@Prop({ default: () => false })
	isCalculation!: boolean

	Operator = Operator;
	currentValue: FormattingRule[] = []

	/** Global settings allow saving formatting rules. */
	globalFormatSettings: FormattingRuleSetting[] = []
	selectedGlobalSetting: FormattingRuleSetting | null = null

	get dragOptions () {
		return {
			animation: 200,
			disabled: false,
			ghostClass: "ghost"
		}
	}

	get iconOptions () {
		return [
			{
				key: "fa-caret-up",
				label: "Caret Up"
			},
			{
				key: "fa-caret-down",
				label: "Caret Down"
			},
			{
				key: "fa-exclamation-triangle",
				label: "Exclamation Triangle"
			},
			{
				key: "fa-exclamation-circle",
				label: "Exclamation Circle"
			}
		]
	}

	get optionsForField () {
		if (!this.column) {
			return []
		}

		// Creating a copy of the operators array to avoid mutating the original one
		let operators = [...(this.column.operators ? this.column.operators : operatorsByFieldType[this.column.dataType])]

		if (!operators) {
			return []
		}

		if (this.isCalculation) {
			operators.push(Operator.InvalidCalculation)
		}

		return operatorLabels.filter((o: any) => operators.indexOf(o.operator) > -1)
	}

	get numericColumns () {
		return this.columns.filter(c => c.fieldDataType === DataType.number)
	}

	async mounted () {
		this.currentValue = this.value

		if (this.disableGlobal === false && !!this.column &&
			(caseColumns.firstOrDefault(cc => cc.key === this.column!.key) === null ||
			caseColumns.firstOrDefault(cc => cc.key === this.column!.key).operators === undefined)
		) {
			let globalFormattingRules = await ApiFormattingRuleSettings.formattingRuleSettingsList()
			if (globalFormattingRules && globalFormattingRules.length > 0) {
				this.globalFormatSettings = (globalFormattingRules)!.filter(rule => rule.dataType === this.column!.dataType)
			}
		}
	}

	@Watch("value")
	onValueChanged (newValue: FormattingRule[]) {
		this.currentValue = newValue
	}

	/** Resets the conditions for each formatting rule if the datatype of the column changes. */
	@Watch("column.dataType")
	private OnDataTypeChange () {
		this.currentValue.forEach((item) => {
			item.value = undefined
			item.operator = null
		})
	}

	dragEnd () {
		this.$emit("input", this.currentValue)
	}

	addRule () {
		this.currentValue.push({
			setTextColor: false,
			setBackgroundColor: false,
			setBar: false,
			setIcon: false,
			barMaximum: null,
			setBarMaximum: null,
			value: null,
			operator: null,
			dynamicValue: false
		})
		this.$emit("input", this.currentValue)
	}

	addGlobalRule (formatSetting: FormattingRuleSetting) {
		const globalRulesetRules = formatSetting.formattingRules ?? []
		const applicableRules = globalRulesetRules.filter(rule => {
			if (rule.operator === Operator.InvalidCalculation) {
				// don't apply 'Invalid Calculation' rules to non-calculation columns
				return this.isCalculation
			}

			return true
		})

		if (applicableRules.length > 0) {
			this.currentValue = this.currentValue.concat(applicableRules)
			this.$emit("input", this.currentValue)
		} else if (globalRulesetRules.length > 0) {
			// display a warning toast if there are rules in the global ruleset but none are applicable
			this.$bvToast.toast("No formatting rules from this global ruleset are applicable here", {
				title: "No Applicable Rules",
				variant: "warning",
				autoHideDelay: 5000
			})
		}
	}

	removeRule (rule: FormattingRule) {
		this.currentValue.remove(rule)
		this.$emit("input", this.currentValue)
	}

	clearValues (rule: FormattingRule) {
		if (!rule.setIcon) {
			rule.icon = null
			rule.iconColor = null
		}

		if (!rule.setBackgroundColor) {
			rule.backgroundColor = null
		}

		if (!rule.setTextColor) {
			rule.textColor = null
		}

		if (!rule.setBar) {
			rule.barColor = null
			rule.barMaximum = null
			rule.setBarMaximum = null
		}
	}

	dynamicValueChanged (rule: FormattingRule, dynamic: boolean) {
		rule.dynamicValue = dynamic !== null ? dynamic : false
	}

	private get showGlobalRulesSelection () {
		return this.globalFormatSettings.length > 0
	}
}
